import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import type { Shop } from '@reims-local/types';
import { authApi, reservationApi, shopApi } from '../lib/api';

interface Slot { iso: string; label: string; remaining: number }

function todayInParis(): string {
  return new Intl.DateTimeFormat('sv-SE', { timeZone: 'Europe/Paris' }).format(new Date());
}

function addDays(date: string, days: number): string {
  const d = new Date(`${date}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return new Intl.DateTimeFormat('sv-SE', { timeZone: 'Europe/Paris' }).format(d);
}

export function ReservationPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [shop, setShop] = useState<Shop | null>(null);
  const [user, setUser] = useState<{ id: string; email: string } | null>(null);
  const [date, setDate] = useState(todayInParis());
  const [slots, setSlots] = useState<Slot[]>([]);
  const [pickedTime, setPickedTime] = useState<string | null>(null);
  const [covers, setCovers] = useState(2);
  const [specialRequest, setSpecialRequest] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [magicSent, setMagicSent] = useState(false);
  const [sendingMagic, setSendingMagic] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    authApi.me().then((res) => {
      if (res?.ok) {
        setUser({ id: res.user.id, email: res.user.email });
        setEmail(res.user.email);
      }
    });
  }, []);

  useEffect(() => {
    if (!slug) return;
    shopApi.getPublic(slug)
      .then((res) => setShop(res.data.shop))
      .catch((err) => setError((err as Error).message));
  }, [slug]);

  useEffect(() => {
    if (!slug || !date) return;
    setPickedTime(null);
    reservationApi.getSlots(slug, date)
      .then((res) => setSlots(res.data))
      .catch((err) => setError((err as Error).message));
  }, [slug, date]);

  const minDate = todayInParis();
  const maxDate = addDays(minDate, 30);

  const validSlots = useMemo(() => slots.filter((s) => s.remaining >= covers), [slots, covers]);

  async function sendMagicLink(e: React.FormEvent) {
    e.preventDefault();
    if (!email.match(/^[^@]+@[^@]+\.[^@]+$/)) {
      setError('Email invalide.');
      return;
    }
    setSendingMagic(true);
    setError(null);
    try {
      await authApi.magicLink(email);
      setMagicSent(true);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSendingMagic(false);
    }
  }

  async function submit() {
    if (!slug || !pickedTime) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await reservationApi.create({
        shop_slug: slug,
        date,
        time: pickedTime,
        covers,
        special_request: specialRequest || undefined,
        buyer_phone: phone || undefined,
      });
      navigate(`/mes-reservations/${res.data.id}`);
    } catch (err) {
      const code = (err as Error).message;
      setError(
        code === 'insufficient_capacity' ? 'Ce créneau est déjà complet. Choisissez-en un autre.'
          : code === 'invalid_slot' ? 'Ce créneau n’est plus disponible.'
          : code === 'reservations_blocked' ? 'Les réservations sont bloquées sur votre compte — contactez-nous.'
          : `Réservation impossible (${code}).`,
      );
    } finally {
      setSubmitting(false);
    }
  }

  if (!shop) {
    return <main className="container">{error ? <div className="alert alert-error">{error}</div> : <p>Chargement…</p>}</main>;
  }

  return (
    <main className="container">
      <h2>Réserver une table — {shop.name}</h2>
      {shop.address && <p className="muted" style={{ marginTop: -12 }}>📍 {shop.address}</p>}
      {error && <div className="alert alert-error">{error}</div>}

      <div className="card">
        <h3 style={{ marginTop: 0 }}>Date & couverts</h3>
        <div className="grid-2">
          <div className="form-row">
            <label>Date</label>
            <input type="date" min={minDate} max={maxDate} value={date} onChange={(e) => setDate(e.target.value)} />
          </div>
          <div className="form-row">
            <label>Nombre de couverts</label>
            <select value={covers} onChange={(e) => setCovers(Number(e.target.value))}>
              {[1,2,3,4,5,6,7,8,9,10,11,12,13,14,15,16,17,18,19,20].map((n) => (
                <option key={n} value={n}>{n} {n > 1 ? 'personnes' : 'personne'}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="card">
        <h3 style={{ marginTop: 0 }}>Créneau</h3>
        {slots.length === 0 ? (
          <p className="muted">Aucun créneau ce jour-là. Changez de date.</p>
        ) : validSlots.length === 0 ? (
          <p className="muted">Tous les créneaux sont complets pour {covers} couvert{covers > 1 ? 's' : ''}. Essayez une autre date ou moins de couverts.</p>
        ) : (
          <div className="slot-grid">
            {slots.map((s) => {
              const disabled = s.remaining < covers;
              return (
                <button
                  key={s.iso}
                  type="button"
                  disabled={disabled}
                  className={pickedTime === s.iso ? 'active' : ''}
                  onClick={() => !disabled && setPickedTime(s.iso)}
                  title={disabled ? `Complet (${s.remaining} restants)` : `${s.remaining} couverts dispo`}
                >
                  {s.label}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {!user ? (
        <div className="card">
          <h3 style={{ marginTop: 0 }}>Vos coordonnées</h3>
          {!magicSent ? (
            <form onSubmit={sendMagicLink}>
              <p className="muted">Un lien de connexion vous sera envoyé par email.</p>
              <div className="form-row">
                <label>Email</label>
                <input required type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
              </div>
              <button className="primary" type="submit" disabled={sendingMagic}>
                {sendingMagic ? 'Envoi…' : 'Recevoir le lien de connexion'}
              </button>
            </form>
          ) : (
            <div className="alert alert-info">
              Email envoyé à <strong>{email}</strong>. Cliquez sur le lien puis revenez ici pour confirmer la réservation.
            </div>
          )}
        </div>
      ) : (
        <div className="card">
          <h3 style={{ marginTop: 0 }}>Vos coordonnées</h3>
          <p className="muted">Connecté en tant que <strong>{user.email}</strong>.</p>
          <div className="form-row">
            <label>Téléphone (pour confirmation et rappel SMS)</label>
            <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="07 12 34 56 78" />
          </div>
          <div className="form-row">
            <label>Demande spéciale (optionnel)</label>
            <textarea rows={2} value={specialRequest} onChange={(e) => setSpecialRequest(e.target.value)} placeholder="Allergies, occasion spéciale, table en terrasse…" />
          </div>
        </div>
      )}

      <button
        className="primary"
        style={{ width: '100%', padding: '14px' }}
        disabled={!user || !pickedTime || submitting}
        onClick={submit}
      >
        {submitting ? 'Envoi…' : 'Confirmer ma réservation'}
      </button>
      {!pickedTime && user && <p className="muted" style={{ textAlign: 'center', marginTop: 8 }}>Choisissez un créneau.</p>}
    </main>
  );
}
